import React from 'react';
import { useRevalidator } from 'react-router-dom';
import { IndexingStatus } from '~/api/graphql';

// A document still UNKNOWN or INDEXING this long after its indexing run started (or,
// before a run starts, after upload) is stuck: its workflow never started or its
// worker died, and polling would never end. Indexing never moves updatedAt, so the
// clock is the run's own start.
const STALE_INDEXING_MS = 60 * 60 * 1000;

type IndexedDocument = {
  createdAt: Date | string;
  indexingStatus: { status: IndexingStatus; startedAt?: Date | string | null };
};

const isIndexingInProgress = ({ createdAt, indexingStatus }: IndexedDocument, now: number) =>
  (indexingStatus.status === IndexingStatus.Unknown ||
    indexingStatus.status === IndexingStatus.Indexing) &&
  now - new Date(indexingStatus.startedAt ?? createdAt).getTime() < STALE_INDEXING_MS;

export function KnowledgeBaseIndexingRefresh(props: { documents: readonly IndexedDocument[] }) {
  const { revalidate, state } = useRevalidator();
  const now = Date.now();
  const hasPendingDocument = props.documents.some((document) =>
    isIndexingInProgress(document, now)
  );

  React.useEffect(() => {
    if (!hasPendingDocument || state !== 'idle') {
      return;
    }

    const timer = window.setTimeout(revalidate, 3000);
    return () => window.clearTimeout(timer);
  }, [hasPendingDocument, revalidate, state]);

  return null;
}
